/**
 * Client performance marks (Phase 0), behind `localStorage["rivets.perf"]`
 * or a dev build. Each is a `performance.mark` pair and `performance.measure`,
 * so they show in the browser's Performance panel and can be read with
 * `performance.getEntriesByType("measure")`:
 *
 *  - `rivets:send→bubble`: Send pressed → the message on screen;
 *  - `rivets:send→echo`: Send pressed → the server's echo;
 *  - `rivets:open-chat→first-row`: a chat selected → its first transcript row;
 *  - `rivets:click-file→content`: a file clicked → its content on screen;
 *  - `rivets:cold-start→first-paint`: navigation start → the first useful
 *    paint (a transcript row or the Home list).
 */

const PERF_STORAGE_KEY = "rivets.perf";

function computeEnabled(): boolean {
  try {
    if (import.meta.env?.DEV) return true;
  } catch {
    /* not a Vite build */
  }
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem(PERF_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

let enabled: boolean | null = null;

export function perfEnabled(): boolean {
  enabled ??= computeEnabled();
  return enabled;
}

/** Test seam. */
export function setPerfEnabled(value: boolean | null): void {
  enabled = value;
}

const open = new Set<string>();

export function perfMark(name: string): void {
  if (!perfEnabled() || typeof performance === "undefined") return;
  try {
    performance.mark(name);
    open.add(name);
  } catch {
    /* marks are best-effort */
  }
}

/** Marks unless a mark of that name is already waiting to be measured (an
 * earlier, more accurate start — the click — wins over a later render). */
export function perfMarkOnce(name: string): void {
  if (open.has(name)) return;
  perfMark(name);
}

/** Measures from `startMark` to now, once; a start that was never marked (or
 * already measured) records nothing. */
export function perfMeasure(measure: string, startMark: string): number | undefined {
  if (!perfEnabled() || typeof performance === "undefined" || !open.has(startMark)) return undefined;
  open.delete(startMark);
  try {
    const entry = performance.measure(measure, startMark);
    performance.clearMarks(startMark);
    return entry?.duration;
  } catch {
    return undefined;
  }
}

let firstPaintMeasured = false;

/** Navigation start → first useful paint, once per page load. */
export function perfFirstUsefulPaint(): void {
  if (firstPaintMeasured || !perfEnabled() || typeof performance === "undefined") return;
  firstPaintMeasured = true;
  try {
    performance.measure("rivets:cold-start→first-paint", { start: 0, end: performance.now() });
  } catch {
    /* best-effort */
  }
}
